import { ChartsAxisContentProps } from "@mui/x-charts";
import { SeriesValueFormatter } from "@mui/x-charts/internals";
import { useTranslation } from "react-i18next";

import { ChartTooltip } from "glue/Chart/components/ChartTooltip";

export const ConsumptionChartTooltip = (props: ChartsAxisContentProps) => {
    const { series, axisValue, dataIndex } = props;
    const { t } = useTranslation();

    if (!dataIndex || !series || series.length === 0) return null;

    const getVerticaData = (vertical: "electricity" | "heat" | "water") =>
        series.reduce(
            (acc, s) => {
                const id = s.id.toString();
                const value = s.data[dataIndex];

                if (!id.includes(vertical) || typeof value !== "number") {
                    return acc;
                }

                if (id.includes("Landlord")) {
                    acc.landlord += value;
                } else if (id.includes("Tenant")) {
                    acc.tenant += value;
                }

                acc.color = s.color || acc.color;

                return acc;
            },
            { landlord: 0, tenant: 0, color: "" }
        );

    const valueFormatter = series[0].valueFormatter as SeriesValueFormatter<
        number | null
    >;
    const electricity = getVerticaData("electricity");
    const heat = getVerticaData("heat");
    const water = getVerticaData("water");

    const verticalItems = (
        label: string,
        vertical: {
            landlord: number;
            tenant: number;
            color: string;
        }
    ) => {
        const total = vertical.landlord + vertical.tenant;
        const getPercentage = (value: number) =>
            `(${Math.round((value / total) * 100)}%)`;

        if (!vertical.landlord && !vertical.tenant) return {};
        return {
            items: [
                {
                    label,
                    value: valueFormatter(vertical.landlord + vertical.tenant, {
                        dataIndex,
                    }),
                    textColor: "text.primary",
                    iconColor: vertical.color,
                    fontWeight: "bold",
                },
                ...(vertical.landlord > 0
                    ? [
                          {
                              fontSize: 14,
                              label: `${t(
                                  "common.landlord",
                                  "Landlord"
                              )} ${getPercentage(vertical.landlord)}`,
                              value: valueFormatter(vertical.landlord, {
                                  dataIndex,
                              }),
                              iconColor: "transparent",
                          },
                      ]
                    : []),
                ...(vertical.tenant > 0
                    ? [
                          {
                              fontSize: 14,
                              label: `${t(
                                  "common.tenant",
                                  "Tenant"
                              )} ${getPercentage(vertical.tenant)}`,
                              value: valueFormatter(vertical.tenant, {
                                  dataIndex,
                              }),
                              iconColor: "transparent",
                          },
                      ]
                    : []),
            ],
        };
    };

    return (
        <ChartTooltip
            sections={[
                { header: axisValue as string },
                verticalItems(
                    t("assessmentOverview.electricity.title", "Electricity"),
                    electricity
                ),
                verticalItems(t("assessmentOverview.heat.title", "Heat"), heat),
                verticalItems(
                    t("assessmentOverview.water.title", "Water"),
                    water
                ),
            ]}
        />
    );
};
